import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';
import { TONE_CLASSES, type Tone } from './status';
import { BRAND, brandFor, type BrandKind } from '../brand';

// Shared UI primitives (both apps). Validated direction: light field + dark
// chrome, blue accent, white 16px cards. Token-driven — no hard-coded colour.

// ── Button ─────────────────────────────────────────────────────────────────
type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  onDark?: boolean;
};
export function Button({ variant = 'primary', size = 'md', onDark, className, ...props }: ButtonProps) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors ' +
    'focus-visible:outline-none disabled:opacity-40 disabled:pointer-events-none';
  const sizes = { sm: 'h-8 px-3 text-sm', md: 'h-9 px-4 text-sm' };
  const variants = {
    primary: 'bg-chrome text-white hover:opacity-90', // dark CTA
    accent: cn('text-accent-fg hover:opacity-90', onDark ? 'bg-accent-dark' : 'bg-accent'),
    secondary: onDark
      ? 'border border-chrome-border bg-chrome-2 text-chrome-fg hover:bg-chrome-3'
      : 'border border-border bg-card text-fg hover:bg-bg',
    ghost: onDark ? 'text-chrome-nav hover:text-chrome-fg' : 'text-muted hover:text-fg',
    danger: 'text-st-overdue-fg hover:bg-st-overdue-bg',
  };
  return <button className={cn(base, sizes[size], variants[variant], className)} {...props} />;
}

// ── Card ───────────────────────────────────────────────────────────────────
export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('rounded-card bg-card shadow-card', className)} {...props} />;
}

// ── Logo ─────────────────────────────────────────────────────────────────────
// Validated variant B. Single-word wordmark Care & Key (never a dot/separator):
// "PREVANT" in Geist 300 at ≥24px / 400 below, "IQ" in Geist 700 tucked tight
// (≈ −6px approach), letter-spacing 0.14em, one colour (#FFFFFF on dark chrome,
// #0B0F17 on light). Symbol: rounded square with a bold "P" — on dark a white
// square with a #0B0F17 P, on light a #0B0F17 square with a white P.
export type LogoVariant = 'dark' | 'light';
// Kept for backward-compat; the brand config is the source of truth.
export const LOGO_INK = BRAND.colors.ink;
export const LOGO_PAPER = BRAND.colors.paper;

export function Logo({
  variant = 'dark',
  size = 34,
  withWordmark = true,
  withTagline = false,
  kind = 'main',
  className,
}: { variant?: LogoVariant; size?: number; withWordmark?: boolean; withTagline?: boolean; kind?: BrandKind; className?: string }) {
  const b = brandFor(kind);
  const { ink, paper } = BRAND.colors;
  const wordColor = variant === 'dark' ? paper : ink;
  const squareBg = variant === 'dark' ? paper : ink;
  const squareFg = variant === 'dark' ? ink : paper;
  const radius = Math.round((size * 10) / 34);
  const wordPx = Math.round(size * 0.52);
  const prefixWeight = wordPx >= 24 ? 300 : 400;
  // one glyph sits large; a two-glyph monogram (CK) is tucked smaller to fit the tile
  const symbolPx = Math.round(size * (b.symbolLetter.length > 1 ? 0.4 : 0.6));
  // Tuck the accent tight only for a single-word mark (Care & Key, OWNERWARD);
  // a multi-word mark (CARE & KEY) keeps a real space before the bold word.
  const tight = !/[\s&]/.test(b.wordmark.prefix);
  return (
    <span className={cn('inline-flex items-center', className)} style={{ gap: Math.round(size * 0.32) }} aria-label={b.name}>
      <span
        aria-hidden
        className="grid shrink-0 place-items-center"
        style={{ width: size, height: size, borderRadius: radius, background: squareBg, color: squareFg, fontSize: symbolPx, fontWeight: 700, lineHeight: 1, letterSpacing: b.symbolLetter.length > 1 ? '-0.04em' : undefined }}
      >
        {b.symbolLetter}
      </span>
      {withWordmark && (
        <span className="inline-flex flex-col" style={{ gap: Math.round(size * 0.08) }}>
          <span aria-hidden style={{ color: wordColor, fontSize: wordPx, letterSpacing: BRAND.letterSpacing, lineHeight: 1, whiteSpace: 'nowrap' }}>
            <span style={{ fontWeight: prefixWeight }}>{b.wordmark.prefix}</span>
            <span style={{ fontWeight: 700, marginLeft: tight ? '-0.12em' : '0.22em' }}>{b.wordmark.accent}</span>
          </span>
          {withTagline && b.tagline && (
            <span aria-hidden style={{ color: wordColor, opacity: 0.55, fontSize: Math.round(size * 0.24), letterSpacing: '0.04em', lineHeight: 1, whiteSpace: 'nowrap' }}>{b.tagline}</span>
          )}
        </span>
      )}
    </span>
  );
}

/** Symbol-only SVG string (for favicons / PWA icons). `maskable` adds a safe-zone
 *  padding so the glyph survives Android's circular/rounded masks. */
export function logoSymbolSvg(variant: LogoVariant = 'dark', px = 512, maskable = false, kind: BrandKind = 'main'): string {
  const { ink, paper } = BRAND.colors;
  const letter = brandFor(kind).symbolLetter;
  const bg = variant === 'dark' ? ink : paper; // icon tile reads best as ink on light launchers
  const fg = variant === 'dark' ? paper : ink;
  const pad = maskable ? px * 0.1 : 0;
  const inner = px - pad * 2;
  const r = Math.round((inner * 10) / 34);
  const fontPx = Math.round(inner * (letter.length > 1 ? 0.4 : 0.6));
  const spacing = letter.length > 1 ? ` letter-spacing="${-inner * 0.02}"` : '';
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${px}" height="${px}" viewBox="0 0 ${px} ${px}">`,
    maskable ? `<rect width="${px}" height="${px}" fill="${bg}"/>` : '',
    `<rect x="${pad}" y="${pad}" width="${inner}" height="${inner}" rx="${r}" ry="${r}" fill="${bg}"/>`,
    `<text x="50%" y="50%" dy="0.02em" text-anchor="middle" dominant-baseline="central" fill="${fg}" font-family="Geist Variable, system-ui, sans-serif" font-weight="700" font-size="${fontPx}"${spacing}>${letter}</text>`,
    `</svg>`,
  ].join('');
}

// ── StatusPill ───────────────────────────────────────────────────────────────
export function StatusPill({ tone, children, className }: { tone: Tone; children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-pill px-2 py-0.5 text-xs font-medium', TONE_CLASSES[tone], className)}>
      {children}
    </span>
  );
}

// ── SegmentedControl ─────────────────────────────────────────────────────────
export function SegmentedControl({
  items,
  active,
  onChange,
  onDark,
}: {
  items: { label: ReactNode; key?: string }[] | string[];
  active: number;
  onChange?: (i: number) => void;
  onDark?: boolean;
}) {
  const list = items.map((it) => (typeof it === 'string' ? { label: it } : it));
  return (
    <div className={cn('inline-flex gap-1 rounded-md p-1', onDark ? 'bg-chrome-2' : 'bg-segmented')}>
      {list.map((it, i) => (
        <button
          key={i}
          onClick={() => onChange?.(i)}
          className={cn(
            'rounded px-3 py-1 text-sm transition-colors',
            i === active
              ? onDark
                ? 'bg-accent-dark text-white'
                : 'bg-card text-fg shadow-card'
              : onDark
                ? 'text-chrome-muted hover:text-chrome-fg'
                : 'text-muted hover:text-fg',
          )}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

// ── Ring (circular SVG gauge) ────────────────────────────────────────────────
export function Ring({
  value,
  size = 64,
  stroke = 6,
  onDark,
  label,
  caption,
}: {
  value: number;
  size?: number;
  stroke?: number;
  onDark?: boolean;
  label?: ReactNode;
  caption?: ReactNode;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, value)) / 100);
  const track = onDark ? 'var(--pv-ring-track-dark)' : 'var(--pv-border)';
  const fill = onDark ? 'var(--pv-accent-dark)' : 'var(--pv-accent)';
  const showLabel = label !== '';
  return (
    <div className="relative inline-grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={fill} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 150ms' }}
        />
      </svg>
      {showLabel && (
        <span className={cn('absolute flex flex-col items-center leading-none', onDark && 'text-white')}>
          <span className="font-mono text-sm">{label ?? `${Math.round(value)}%`}</span>
          {caption && <span className={cn('mt-0.5 text-2xs', onDark ? 'text-chrome-muted' : 'text-muted')}>{caption}</span>}
        </span>
      )}
    </div>
  );
}

// ── Sparkbars (mini bar chart) ───────────────────────────────────────────────
export function Sparkbars({
  values,
  height = 22,
  barWidth = 6,
  gap = 3,
  color,
}: {
  values: number[];
  height?: number;
  barWidth?: number;
  gap?: number;
  color?: string;
}) {
  const max = Math.max(...values, 1);
  const width = values.length * (barWidth + gap) - gap;
  const fill = color ?? 'var(--pv-accent)';
  return (
    <svg width={width} height={height} role="img" aria-hidden>
      {values.map((v, i) => {
        const h = (v / max) * height;
        return <rect key={i} x={i * (barWidth + gap)} y={height - h} width={barWidth} height={h} rx={2} fill={fill} opacity={0.85} />;
      })}
    </svg>
  );
}

// ── ProgressBar ──────────────────────────────────────────────────────────────
export function ProgressBar({ value, onDark }: { value: number; onDark?: boolean }) {
  return (
    <div className={cn('h-1.5 w-full overflow-hidden rounded-full', onDark ? 'bg-chrome-border' : 'bg-divider')}>
      <div className="h-full rounded-full bg-accent" style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}

// ── KpiCard (light + dark) ───────────────────────────────────────────────────
export function KpiCard({
  label,
  value,
  sub,
  dark,
  children,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  dark?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className={cn('rounded-card p-[18px]', dark ? 'bg-chrome text-white' : 'bg-card text-fg shadow-card')}>
      <div className="flex items-start gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className={cn('text-xs', dark ? 'text-chrome-muted' : 'text-muted')}>{label}</span>
          <span className="font-mono text-[26px] font-medium leading-none">{value}</span>
          {sub && <span className={cn('text-xs', dark ? 'text-chrome-muted' : 'text-muted')}>{sub}</span>}
        </div>
        {children}
      </div>
    </div>
  );
}

// ── DarkHeader (detail-page header band) ─────────────────────────────────────
export function DarkHeader({
  breadcrumb,
  title,
  meta,
  actions,
  children,
  className,
}: {
  breadcrumb?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('bg-chrome p-6 text-white', className)}>
      {breadcrumb && <div className="mb-2 font-mono text-xs text-chrome-muted">{breadcrumb}</div>}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[22px] font-semibold tracking-tight">{title}</h1>
          {meta && <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-chrome-muted">{meta}</div>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

// ── Timeline (activity / history) ────────────────────────────────────────────
export function Timeline({
  items,
}: {
  items: { time?: ReactNode; title: ReactNode; desc?: ReactNode; color?: string }[];
}) {
  return (
    <ol className="space-y-4">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3">
          <span
            className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ background: it.color ?? 'var(--pv-accent)', boxShadow: `0 0 0 4px ${it.color ?? 'var(--pv-accent)'}22` }}
            aria-hidden
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-sm font-medium">{it.title}</span>
            {it.desc && <span className="text-sm text-muted">{it.desc}</span>}
            {it.time && <span className="font-mono text-xs text-muted">{it.time}</span>}
          </div>
        </li>
      ))}
    </ol>
  );
}

// ── DataTable (list views) ───────────────────────────────────────────────────
export function DataTable({ columns, rows }: { columns: ReactNode[]; rows: ReactNode[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-left text-xs text-muted">
            {columns.map((c, i) => (
              <th key={i} className="px-3 py-3 font-medium">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-divider transition-colors hover:bg-bg">
              {r.map((cell, j) => (
                <td key={j} className="px-3 py-3.5">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
